import './EditDialog.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputMask } from 'primereact/inputmask';
import { InputText } from 'primereact/inputtext';
import { Fragment, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Locker, LockerFormData } from '../../../../../types/api/lockers';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors';
import {
  getDays,
  getInitialValues,
  getValidationSchema,
} from './EditDialog.functions';

type Props = {
  data: Locker | undefined;
  handleSubmitForm: (data: LockerFormData) => void;
};

function FormContainer({ data, handleSubmitForm }: Props) {
  const { t } = useTranslation();

  const days = useMemo(() => {
    const days = getDays(t);

    return days.map((day) => ({ label: day.label, value: day.dayOfWeek }));
  }, [t]);

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const defaultValues = useMemo(() => getInitialValues(data!), [data]);

  const methods = useForm<LockerFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    // reset,
    getValues,
    formState: { errors },
  } = methods;
  console.log('getValues ', getValues());
  console.log('errors ', errors);
  // useEffect(() => {
  //   reset(defaultValues);
  // }, [defaultValues, reset]);

  const { fields, append, remove } = useFieldArray<
    LockerFormData,
    'maintenance_windows'
  >({
    control,
    name: 'maintenance_windows',
  });

  return (
    <FormProvider {...methods}>
      <form id="locker-edit-form" onSubmit={handleSubmit(handleSubmitForm)}>
        <div className="info-sections">
          <FieldWithErrors name="title" label={t('Name')}>
            <Controller
              name="title"
              render={({ field }) => (
                <InputText
                  id="title"
                  name="title"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={true}
                />
              )}
            />
          </FieldWithErrors>
          <FieldWithErrors name="address.rawName" label={t('Address')}>
            <Controller
              name="address.rawName"
              render={({ field }) => (
                <InputText
                  id="address.rawName"
                  name="address.rawName"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={true}
                />
              )}
            />
          </FieldWithErrors>
          <FieldWithErrors name="createdAt" label={t('Created At')}>
            <Controller
              name="createdAt"
              render={({ field }) => (
                <InputText
                  id="createdAt"
                  name="createdAt"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={true}
                />
              )}
            />
          </FieldWithErrors>
          <FieldWithErrors name="numBoxes" label={t('Number of boxes')}>
            <Controller
              name="numBoxes"
              render={({ field }) => (
                <InputText
                  id="numBoxes"
                  name="numBoxes"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={true}
                />
              )}
            />
          </FieldWithErrors>
        </div>
        <div className="maintenance-windows">
          <h4>{t('Maintenance windows')}</h4>
          {fields?.map((iv, idx) => (
            <Fragment key={iv.id}>
              <div key={iv.id} className="time-row">
                <FieldWithErrors
                  name={`maintenance_windows.${idx}.start_day_of_week`}
                  label={t('Day')}
                >
                  <Controller
                    name={`maintenance_windows.${idx}.start_day_of_week`}
                    render={({ field }) => (
                      <Dropdown
                        id={`maintenance_windows.${idx}.start_day_of_week`}
                        name={`maintenance_windows.${idx}.start_day_of_week`}
                        options={days}
                        value={field.value}
                        onChange={field.onChange}
                        className="data-cy-start_day_of_week"
                      />
                    )}
                  />
                </FieldWithErrors>

                <FieldWithErrors
                  name={`maintenance_windows.${idx}.start_time`}
                  label={false}
                >
                  <Controller
                    name={`maintenance_windows.${idx}.start_time`}
                    render={({ field }) => (
                      <InputMask
                        {...field}
                        id={`maintenance_windows[${idx}].start_time`}
                        name={`maintenance_windows.${idx}.start_time`}
                        mask="99:99"
                        placeholder="HH:MM"
                        className="time-input"
                      />
                    )}
                  />
                </FieldWithErrors>

                <FieldWithErrors
                  name={`maintenance_windows.${idx}.end_day_of_week`}
                  label={t('Day')}
                >
                  <Controller
                    name={`maintenance_windows.${idx}.end_day_of_week`}
                    render={({ field }) => (
                      <Dropdown
                        id={`maintenance_windows.${idx}.end_day_of_week`}
                        name={`maintenance_windows.${idx}.end_day_of_week`}
                        options={days}
                        value={field.value}
                        onChange={field.onChange}
                        className="data-cy-end_day_of_week"
                      />
                    )}
                  />
                </FieldWithErrors>

                <FieldWithErrors
                  name={`maintenance_windows.${idx}.end_time`}
                  label={false}
                >
                  <Controller
                    name={`maintenance_windows.${idx}.end_time`}
                    render={({ field }) => (
                      <InputMask
                        {...field}
                        id={`maintenance_windows[${idx}].end_time`}
                        name={`maintenance_windows.${idx}.end_time`}
                        mask="99:99"
                        placeholder="HH:MM"
                        className="time-input"
                      />
                    )}
                  />
                </FieldWithErrors>

                <Button
                  type="button"
                  label="×"
                  className="remove-interval-btn p-button-danger"
                  onClick={() => remove(idx)}
                />
              </div>
            </Fragment>
          ))}
          <Button
            type="button"
            label={t('Add maintenance window')}
            className="p-button-primary"
            onClick={() =>
              append({
                id: fields?.length + 1,
                start_day_of_week: 1,
                start_time: '',
                end_day_of_week: 1,
                end_time: '',
              })
            }
          />
        </div>
      </form>
    </FormProvider>
  );
}

export default FormContainer;
